'use client';

import { useFlag } from '@unleash/proxy-client-react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { v4 } from 'uuid';

import useAccessBadge, { UserAccessBadge } from '../../hooks/useAccessBadge';
import useGeoLocation from '../../hooks/useGeoLocation';

const isTargetUserProfile = ({
  userAccessBadge,
  isUS,
}: {
  userAccessBadge: UserAccessBadge | undefined;
  isUS: boolean;
  country: string | null;
}) => {
  const signedUpRecently =
    userAccessBadge?.user.createdAt &&
    new Date(userAccessBadge.user.createdAt).getTime() < Date.now() - 1000 * 60 * 60 * 1; // 1h

  return signedUpRecently && isUS;
};

export function getRecordingsUrl({
  userAccessBadge,
  isUS,
  country,
}: {
  userAccessBadge: UserAccessBadge | undefined;
  isUS: boolean;
  country: string | null;
}) {
  if (!userAccessBadge || !isTargetUserProfile({ userAccessBadge, isUS, country }))
    return undefined;
  const baseUrl = 'https://insights.hotjar.com/sites/5062971/playbacks';
  const urlParams = `sort_by=-created&date=%7B%22DAYS_AGO%22:%7B%22created%22:15%7D%7D&filters=%7B%22EQUAL%22:%7B%22user_attributes.str.user_id%22:%22${userAccessBadge.user.id}%22%7D%7D`;
  return `${baseUrl}?${urlParams}`;
}

/**
 * Flows that ask to be recorded even when the teacher isn't in the default
 * profile — the widening `?curriculumFit=1` gets from the URL, for flows that
 * have no URL of their own. The passage upload is the case: it runs inside a
 * modal on Text Dependent Analysis and inside the test-prep generator form, so
 * there is no route or query for `HotjarSnippet` to read.
 *
 * Module state with subscribers rather than a query param because asking is a
 * side effect of a click deep in a form, and pushing to the router there would
 * re-render the page under an open modal to say something only analytics cares
 * about. Latched on: a session that asked stays asked.
 *
 * Asking is not being recorded. The request only reaches the same gate the rest
 * of this file describes — `HOTJAR_MONITORING`, US-only — so it widens *who*
 * within that scope, exactly as the curriculum-fit subpage does, and never
 * turns Hotjar on for someone it was off for.
 */
let hasRequestedRecording = false;
const recordingRequestListeners = new Set<() => void>();

/**
 * Ask for this session to be recorded. Safe to call repeatedly and from
 * anywhere; SSR-safe. Takes effect on the next render of `HotjarSnippet`, so
 * anything already fired this tick is not in the recording — call it when the
 * teacher enters the flow, not at the moment you want captured.
 */
export function requestHotjarRecording() {
  if (hasRequestedRecording) return;
  hasRequestedRecording = true;
  recordingRequestListeners.forEach(listener => listener());
}

/** Test seam: the module state outlives a component, so it has to be resettable. */
export function resetHotjarRecordingRequest() {
  hasRequestedRecording = false;
  recordingRequestListeners.clear();
  pendingEvents = [];
}

function subscribeToRecordingRequest(listener: () => void) {
  recordingRequestListeners.add(listener);
  return () => {
    recordingRequestListeners.delete(listener);
  };
}

/**
 * Events fired before `window.hj` existed, waiting for it.
 *
 * A caller that asks for recording and tags the same moment would otherwise
 * lose the tag: `requestHotjarRecording` only takes effect on the snippet's
 * next render, so `window.hj` is still missing for the rest of that tick — and
 * it is precisely the teachers this widening exists for who have no snippet
 * yet. Held here and flushed the moment the snippet installs.
 *
 * Bounded because a page where Hotjar never loads would otherwise grow this
 * forever; past the cap the oldest go, since a replay filter is worth less the
 * further back it points.
 */
const MAX_PENDING_EVENTS = 20;
let pendingEvents: string[] = [];

/**
 * Hand Hotjar everything held while it was absent. Called straight after the
 * snippet is appended: an inline `<script>` runs synchronously on insertion and
 * its first act is to install the `hj` stub, which queues until the real script
 * downloads — so by here there is something to receive them.
 *
 * These land at the very start of the recording rather than at the moment they
 * describe: the recording began when the script did. They make the session
 * findable, which is what a tag is for; they do not put the earlier click back
 * into the replay, because it happened before there was one.
 */
function flushPendingHotjarEvents() {
  if (!pendingEvents.length) return;
  const queued = pendingEvents;
  pendingEvents = [];
  queued.forEach(event => window.hj?.('event', event));
}

/**
 * Fire a Hotjar event so the current session's recording is findable by that
 * moment (Hotjar → Recordings → filter by event).
 *
 * `window.hj` is installed once by `HotjarSnippet` (app-level, after geo + the
 * access badge resolve). Until then the event is held rather than dropped, so a
 * caller may tag a moment and ask for recording in the same breath. Nothing is
 * ever sent to Hotjar that the snippet's own gate did not admit: an event held
 * on a session that never gets a snippet is simply never delivered. SSR-safe.
 */
export function trackHotjarEvent(event: string) {
  if (typeof window === 'undefined') return;
  if (!window.hj) {
    pendingEvents = [...pendingEvents.slice(-(MAX_PENDING_EVENTS - 1)), event];
    return;
  }
  window.hj('event', event);
}

const HotjarSnippet = () => {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { isUS, country, isLoading: isGeoLoading } = useGeoLocation();
  const router = useRouter();
  const isHotjarEnabled = useFlag('HOTJAR_MONITORING');
  const isSignupPage = router.pathname === '/signup';
  // `false` on the server: the request is a client-side act, and rendering it
  // as asked would mismatch the first client render.
  const isRecordingRequested = useSyncExternalStore(
    subscribeToRecordingRequest,
    useCallback(() => hasRequestedRecording, []),
    useCallback(() => false, []),
  );

  const [offlineSessionId] = useState<string>(String(v4()));

  const [utmTracking, setUtmTracking] = useState<{
    source: string;
    content: string;
  }>({
    source: '',
    content: '',
  });

  useEffect(() => {
    if (!userAccessBadge) return;
    if (isGeoLoading) return;
    function formattedStringOrNull(value: string | null | undefined) {
      if (!!value) {
        const escapedValue = String(value).replace(/"/g, '\\"');
        return `"${escapedValue}"`;
      } else {
        return null;
      }
    }

    function getFormattedScriptContent() {
      const scriptContent = `
        (function(h,o,t,j,a,r){
          try {
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:5062971,hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            r.onload = function() {
              try {
                if (window.hj) {
                  window.hj('identify', ${formattedStringOrNull(
                    userAccessBadge?.user.id || offlineSessionId,
                  )}, {
                    schoolId: ${formattedStringOrNull(userAccessBadge?.organization?.id)},
                    schoolCity: ${formattedStringOrNull(
                      userAccessBadge?.organization?.city,
                    )},
                    schoolState: ${formattedStringOrNull(
                      userAccessBadge?.organization?.region,
                    )},
                    schoolDistrictId: ${formattedStringOrNull(
                      userAccessBadge?.organization?.parent?.id,
                    )},
                    subjects: ${formattedStringOrNull(
                      Array.isArray(userAccessBadge?.user.profileInfo?.subjects)
                        ? userAccessBadge?.user.profileInfo?.subjects.join(',')
                        : userAccessBadge?.user.profileInfo?.subjects,
                    )},
                    coarseLicense: ${formattedStringOrNull(
                      userAccessBadge?.license.coarseLevel,
                    )},
                    fineLicense: ${formattedStringOrNull(
                      userAccessBadge?.license.fineLevel,
                    )},
                    schoolRole: ${formattedStringOrNull(
                      userAccessBadge?.user.profileInfo?.role,
                    )},
                    createdAt: ${userAccessBadge?.user.createdAt || null},
                    createdAtISO: ${formattedStringOrNull(
                      userAccessBadge?.user.createdAt
                        ? new Date(userAccessBadge?.user.createdAt).toISOString()
                        : null,
                    )},
                    utmSource: ${formattedStringOrNull(utmTracking.source)},
                    utmContent: ${formattedStringOrNull(utmTracking.content)},
                    isSignupProcess: ${isSignupPage},
                    selectedOrganizationCountry: ${formattedStringOrNull(
                      userAccessBadge?.organization?.country,
                    )},
                  });
                  console.log('Hotjar identify event set');
                }
              } catch (e) {
                console.error('Error during Hotjar identify');
              }
            };
            r.onerror = function(e) {
              console.error('Error running Hotjar script');
            };
            a.appendChild(r);
          } catch (e) {
            console.error('Error loading Hotjar script');
          }
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `;
      return scriptContent;
    }

    try {
      const currentUtmSource = router?.query?.utm_source;
      const currentUtmContent = router?.query?.utm_content;
      setUtmTracking({
        source: !!currentUtmSource ? String(currentUtmSource) : utmTracking.source,
        content: !!currentUtmContent ? String(currentUtmContent) : utmTracking.content,
      });

      // Also record any US teacher who opens the curriculum-fit subpage — reached
      // via the "How does Curipod fit my Curriculum?" banner, which pushes
      // ?curriculumFit=1 — not just recently-signed-up users, so we capture the
      // full banner-click journey. Still gated on HOTJAR_MONITORING (the master
      // Hotjar switch) and kept US-only, matching the existing recording scope.
      const isCurriculumFitVisitor = router.query.curriculumFit === '1' && isUS;
      // And any US teacher who has entered a flow that asked to be recorded —
      // today the passage upload, which has no URL of its own to read. Same
      // widening as the line above, reached through `requestHotjarRecording`
      // instead of a query param; `HOTJAR_MONITORING` and US-only still hold.
      const isRequestedRecordingVisitor = isRecordingRequested && isUS;
      const shouldTrackUser =
        isHotjarEnabled &&
        (isTargetUserProfile({ userAccessBadge, isUS, country }) ||
          isCurriculumFitVisitor ||
          isRequestedRecordingVisitor);

      let trackingScript = document.getElementById('hotjar-identify');
      if (!trackingScript && shouldTrackUser) {
        trackingScript = document.createElement('script');
        trackingScript.id = 'hotjar-identify';
        trackingScript.textContent = getFormattedScriptContent();
        document.head.appendChild(trackingScript);
        flushPendingHotjarEvents();
      } else if (trackingScript && !shouldTrackUser) {
        trackingScript.remove();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [
    userAccessBadge,
    isHotjarEnabled,
    router,
    offlineSessionId,
    utmTracking.source,
    utmTracking.content,
    isSignupPage,
    isUS,
    country,
    isGeoLoading,
    isRecordingRequested,
  ]);

  return <div></div>;
};

export default HotjarSnippet;
